import {
  createApiRef,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { ApproveData, RequestRow, StatusDetail } from './types';

export const requestTrackerApiRef = createApiRef<RequestTrackerApi>({
  id: 'plugin.request-tracker.service',
});

export interface RequestTrackerApi {
  getMyRequests(): Promise<RequestRow[]>;
  getPendingApprovals(): Promise<RequestRow[]>;
  getAllRequests(): Promise<RequestRow[]>;
  getRequestDetail(requestId: string): Promise<StatusDetail>;
  getApprovalInfo(token: string): Promise<ApproveData>;
  submitDecision(
    token: string,
    approved: boolean,
    comment: string,
  ): Promise<void>;
}

export class RequestTrackerClient implements RequestTrackerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('request-tracker');
    const { token } = await this.identityApi.getCredentials();
    const res = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ||
          `Request failed with status ${res.status}`,
      );
    }
    return res.json() as Promise<T>;
  }

  getMyRequests(): Promise<RequestRow[]> {
    return this.request<RequestRow[]>('/requests/my');
  }

  getPendingApprovals(): Promise<RequestRow[]> {
    return this.request<RequestRow[]>('/requests/pending');
  }

  getAllRequests(): Promise<RequestRow[]> {
    return this.request<RequestRow[]>('/requests');
  }

  getRequestDetail(requestId: string): Promise<StatusDetail> {
    return this.request<StatusDetail>(
      `/requests/${encodeURIComponent(requestId)}`,
    );
  }

  getApprovalInfo(token: string): Promise<ApproveData> {
    return this.request<ApproveData>(`/approve/${encodeURIComponent(token)}`);
  }

  async submitDecision(
    token: string,
    approved: boolean,
    comment: string,
  ): Promise<void> {
    await this.request<unknown>(`/approve/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify({ approved, comment }),
    });
  }
}
